import React from "react";
import { useTranslation } from 'react-i18next';
import { Button, Col, Form, Input, message, Modal, Row, Select, Tabs, Typography } from "antd";
import { CloseOutlined, CopyOutlined } from '@ant-design/icons'
import { feature, featureCollection } from '@turf/turf';
import { PlantationPolygon } from '../../../constants/string'
import { parseCoordinates, parseGeoJSON } from './utils'
import { objStringify } from "../../../frameworks/Util";
import { useTransition } from "../../../hooks/useTransition";

export default function AddPolygonModal({ open, onClose, data, onImport }) {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [messageApi, contextHolder] = message.useMessage()
  const [isLoading, startLoading] = useTransition()
  const [mode, setMode] = React.useState('geojson')
  const [type, setType] = React.useState('all')

  const polygons = React.useMemo(() => {
    const filtered = data.filter(d => type === 'all' || d.type === type)

    if (mode === 'geojson') {
      const fcs = featureCollection(filtered.map(d => feature(d.geojson, {type: d.type})))
      return objStringify(fcs)
    }
    
    const coords = filtered.map(d => d.geojson.coordinates)
    return objStringify(coords)
  }, [data, type, mode])

  const validateCoords = (coords) => {
    form.setFields([{name: 'polygon', errors: []}])
    try {
      if (coords) {
        coords = JSON.parse(coords)
        return parseCoordinates(coords)
      }
      return []
    }
    catch(_) {
      form.setFields([{name: 'polygon', errors: [t('invalid_coordinates')]}])
      throw new Error()
    }
  }

  const validateGeoJSON = (geojsons) => {
    form.setFields([{name: 'polygon', errors: []}])
    try {
      if (geojsons) {
        geojsons = JSON.parse(geojsons)
        if (!Array.isArray(geojsons)) geojsons = [geojsons]
        return [...parseGeoJSON(geojsons)]
      }
      return []
    }
    catch(_) {
      form.setFields([{name: 'polygon', errors: [t('invalid_geojson')]}])
      throw new Error()
    }
  }

  const copyData = () => {
    if (navigator?.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(polygons)
      messageApi.success(t('copy_successful'))
    }
  }

  const handleCreatePolygon = async () => {
    try {
      const data = await form.validateFields()
      const validator = mode === 'geojson' ? validateGeoJSON : validateCoords

      onImport?.(data.type, validator(data.polygon))
      onClose?.()
    }
    catch (_) {
      messageApi.error(t("layer_import_error"))
    }
  }

  React.useEffect(() => {
    if (open) {
      form.resetFields();
      setMode('geojson')
      setType('all')
    }
  },[open])

  return(
    <Modal
      okText={t("ok")}
      cancelText={t("cancel")}
      width={'60%'}
      open={open}
      closeIcon={null}
      onOk={() => startLoading(handleCreatePolygon)}
      confirmLoading={isLoading}
      onCancel={onClose}
      title={
        <Tabs
          activeKey={mode}
          onChange={(mode) => {
            setMode(mode)
            form.setFieldValue('polygon', null)
          }}
          tabBarExtraContent={{ right: <Button type='text' icon={<CloseOutlined/>} onClick={onClose}/> }}
          tabBarStyle={{ marginBottom: 0 }}
          items={[
            {key: 'geojson', label: 'GeoJSON'},
            {key: 'coords', label: t("coordinates")},
          ]}
        />
      }
    >
      {contextHolder}
      <Form form={form} layout="vertical" autoComplete="off">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Tabs
              activeKey={type}
              onChange={(type) => setType(type)}
              tabBarExtraContent={{ right: <Button icon={<CopyOutlined/>} onClick={copyData}>{t('copy')}</Button> }}
              items={[
                { key: 'all', label: t("plantation_layers.all") },
                ...Object.values(PlantationPolygon.Types).map(type => ({ key: type, label: t(PlantationPolygon.Labels[type]) }))
              ]}
            />
            <Input.TextArea readOnly rows={8} value={polygons}/>
          </Col>

          <Col span={24}>
            <Typography>{t('layer_type')}</Typography>
            <Form.Item noStyle name="type" initialValue={PlantationPolygon.Types.TOTAL}>  
              <Select
                style={{ width: '100%' }}
                options={Object.values(PlantationPolygon.Types).map(type => ({ value: type, label: t(PlantationPolygon.Labels[type]) }))}
              />
            </Form.Item>
          </Col>
        
          <Col span={24}>
            <Typography>{mode === 'geojson' ? t('layer_import_via_geojson') : t('layer_import_via_coordinates')}</Typography>
            <Form.Item name="polygon">
              <Input.TextArea rows={8} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  )
}